import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthState, useAuth, AuthContext } from './hooks/useAuth'
import { useAccess } from './hooks/useAccess'
import Landing from './pages/Landing'
import Login from './pages/Login'
import Terms from './pages/Terms'
import Privacy from './pages/Privacy'
import AppMain from './pages/AppMain'
import Settings from './pages/Settings'
import AuthCallback from './pages/AuthCallback'
import Waitlist from './pages/Waitlist'
import './App.css'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

// Always-public routes — never gated.
const PUBLIC_PATHS = new Set(['/terms', '/privacy', '/auth/callback', '/waitlist'])

function GatedRoutes() {
  const location = useLocation()
  const { status } = useAccess()

  // Always allow legal + auth callback + the waitlist page itself.
  if (PUBLIC_PATHS.has(location.pathname)) {
    return (
      <Routes>
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/waitlist" element={<Waitlist />} />
      </Routes>
    )
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (status === 'denied') {
    return <Waitlist />
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppMain />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  const auth = useAuthState()

  return (
    <AuthContext.Provider value={auth}>
      <Router>
        <div className="App">
          <GatedRoutes />
        </div>
      </Router>
    </AuthContext.Provider>
  )
}

export default App
